import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Search, Edit2, Trash2, Building2, ChevronRight, Download } from 'lucide-react';
import { cn } from '../lib/utils';

const emptyForm = () => ({ name: '', address: '', email: '', phone: '', notes: '' });

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [properties, setProperties] = useState([]);

  const load = async () => {
    const [c, p] = await Promise.all([api.get('/customers'), api.get('/properties')]);
    setCustomers(c.data);
    setProperties(p.data);
  };

  useEffect(() => {
    load().catch(() => toast.error('Fehler beim Laden')).finally(() => setLoading(false));
  }, []);

  const normalizedSearch = search.trim().toLowerCase();
  const filtered = customers.filter(c => !normalizedSearch || [
    c.name, c.address, c.email, c.phone, c.notes,
  ].some(v => (v || '').toString().toLowerCase().includes(normalizedSearch)));

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDlgOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, address: c.address || '', email: c.email || '', phone: c.phone || '', notes: c.notes || '' }); setDlgOpen(true); };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name ist pflicht'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success('Aktualisiert');
      } else {
        await api.post('/customers', form);
        toast.success('Verwaltung erstellt');
      }
      setDlgOpen(false);
      await load();
    } catch { toast.error('Fehler'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    await api.delete(`/customers/${id}`);
    toast.success('Gelöscht');
    await load();
    if (selected?.id === id) setSelected(null);
  };

  const handleExport = () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('yh_token');
    fetch(`${BACKEND_URL}/api/export/customers`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'kunden.csv'; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error('Export fehlgeschlagen'));
  };

  const customerProperties = selected ? properties.filter(p => p.customer_id === selected.id) : [];

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="customers-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl">Verwaltungen</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{customers.length} Verwaltungen</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExport} data-testid="export-customers-btn" className="rounded-sm gap-2">
              <Download className="w-4 h-4" />CSV
            </Button>
            <Button onClick={openNew} data-testid="new-customer-btn" className="rounded-sm gap-2">
              <Plus className="w-4 h-4" />Neue Verwaltung
            </Button>
          </div>
        </div>

        <div className="flex gap-4">
          {/* List */}
          <div className="flex-1 min-w-0">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Verwaltungen suchen..." className="pl-9 rounded-sm" data-testid="customer-search" />
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-40"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
            ) : (
              <div className="space-y-1.5">
                {filtered.map(c => (
                  <div
                    key={c.id}
                    data-testid={`customer-row-${c.id}`}
                    onClick={() => setSelected(c)}
                    className={cn("flex items-center justify-between p-4 border rounded-sm cursor-pointer transition-all stagger-item",
                      selected?.id === c.id ? "border-primary bg-primary/5" : "border-border/60 bg-card hover:border-primary/30 hover:shadow-sm"
                    )}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-foreground">{c.name}</p>
                      {c.email && <p className="text-xs text-muted-foreground truncate">{c.email}</p>}
                      {c.address && <p className="text-xs text-muted-foreground truncate">{c.address}</p>}
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <button onClick={e => { e.stopPropagation(); openEdit(c); }} className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-sm hover:bg-muted">
                        <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleDelete(c.id); }} className="text-muted-foreground hover:text-destructive transition-colors p-1.5 rounded-sm hover:bg-destructive/10" data-testid={`delete-customer-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Keine Verwaltungen gefunden</p>}
              </div>
            )}
          </div>

          {/* Detail Panel */}
          {selected && (
            <div className="w-72 shrink-0 space-y-4" data-testid="customer-detail-panel">
              <div className="bg-card border border-border/60 rounded-sm p-5">
                <h3 className="font-heading font-bold text-base mb-3">{selected.name}</h3>
                {selected.address && <p className="text-sm text-muted-foreground mb-1">{selected.address}</p>}
                {selected.email && <p className="text-sm text-muted-foreground mb-1">{selected.email}</p>}
                {selected.phone && <p className="text-sm text-muted-foreground mb-1">{selected.phone}</p>}
                {selected.notes && <p className="text-sm text-muted-foreground mt-2 pt-2 border-t border-border/60">{selected.notes}</p>}
              </div>
              <div className="bg-card border border-border/60 rounded-sm p-5">
                <h4 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" strokeWidth={1.5} />
                  Liegenschaften ({customerProperties.length})
                </h4>
                {customerProperties.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Keine Liegenschaften</p>
                ) : (
                  <div className="space-y-1.5">
                    {customerProperties.map(p => (
                      <div key={p.id} className="text-xs p-2 bg-muted rounded-sm">
                        <p className="font-medium text-foreground">{p.address}</p>
                        {p.object_number && <p className="text-muted-foreground">{p.object_number}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Verwaltung bearbeiten' : 'Neue Verwaltung'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Name *</Label><Input data-testid="customer-form-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-sm" /></div>
            <div className="space-y-1.5"><Label>Adresse</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>E-Mail</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="rounded-sm" /></div>
              <div className="space-y-1.5"><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="rounded-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label>Notizen</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="rounded-sm resize-none" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="save-customer-btn">{saving ? 'Speichern...' : 'Speichern'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
